'use client';
import React from 'react';
import { Mic, Brain, Sun, ArrowRight, Sparkles } from 'lucide-react';
import Link from 'next/link';

const steps = [
  {
    number: '01',
    icon: Mic,
    title: 'Talk naturally.',
    desc: 'Type or speak. Tell AgentOS about a client call, a showing, a follow-up, or anything on your mind. No forms. No data entry. Just conversation.',
    color: '#00D4FF',
    gradient: 'linear-gradient(135deg, #00D4FF, #0099CC)',
    glow: 'rgba(0,212,255,0.35)',
  },
  {
    number: '02',
    icon: Brain,
    title: 'AgentOS remembers everything.',
    desc: 'It builds context across your entire business. Every client. Every conversation. Every task. Every promise. Nothing gets lost.',
    color: '#A78BFA',
    gradient: 'linear-gradient(135deg, #A78BFA, #7C3AED)',
    glow: 'rgba(167,139,250,0.30)',
  },
  {
    number: '03',
    icon: Sun,
    title: 'Wake up prepared.',
    desc: 'Every morning, AgentOS tells you exactly who needs your attention, what\'s due, and where your best opportunities are. Start every day with clarity.',
    color: '#00D4FF',
    gradient: 'linear-gradient(135deg, #00D4FF, #A78BFA)',
    glow: 'rgba(0,212,255,0.25)',
  },
];

export default function HowItWorksSection() {
  return (
    <section id="how-it-works" className="py-24 section-alt-bg relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(0,212,255,0.04) 0%, transparent 70%)' }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <div className="section-badge mb-4">
            <Sparkles size={12} />
            How It Works
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4"
            style={{ color: '#F0F4FF' }}
          >
            Simple by design.{' '}
            <span className="gradient-cyan-text">Powerful by default.</span>
          </h2>
          <p className="text-lg max-w-xl mx-auto" style={{ color: 'rgba(240,244,255,0.55)' }}>
            Three steps. That's it.
          </p>
        </div>

        <div className="relative">
          {/* Connector Line */}
          <div
            className="hidden lg:block absolute top-10 h-0.5 mx-auto"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(0,212,255,0.25) 20%, rgba(167,139,250,0.25) 50%, rgba(0,212,255,0.25) 80%, transparent)',
              width: 'calc(100% - 200px)',
              left: '100px',
            }}
          />

          <div className="grid sm:grid-cols-3 gap-10 lg:gap-16">
            {steps?.map((step, idx) => (
              <div key={step?.number} className="relative text-center">
                {/* Step Icon */}
                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 relative"
                  style={{
                    background: step?.gradient,
                    boxShadow: `0 8px 24px ${step?.glow}`,
                  }}
                >
                  <step.icon size={30} className="text-white" strokeWidth={2} />
                  <div
                    className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold"
                    style={{
                      background: '#0B0F19',
                      color: step?.color,
                      border: `2px solid ${step?.color}`,
                      fontFamily: 'JetBrains Mono, monospace',
                    }}
                  >
                    {idx + 1}
                  </div>
                </div>

                <div
                  className="text-xs font-bold uppercase tracking-widest mb-3"
                  style={{ color: step?.color, fontFamily: 'JetBrains Mono, monospace' }}
                >
                  Step {step?.number}
                </div>
                <h3 className="text-xl font-extrabold mb-3 leading-snug" style={{ color: '#F0F4FF' }}>{step?.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,244,255,0.55)' }}>{step?.desc}</p>

                {/* Arrow between steps */}
                {idx < steps?.length - 1 && (
                  <div className="hidden lg:flex absolute top-8 -right-8 z-10 items-center justify-center">
                    <ArrowRight size={16} style={{ color: 'rgba(0,212,255,0.30)' }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-14">
          <a
            href="https://agentos-ai-vgojf93.public.builtwithrocket.new/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
          >
            Start Free Trial
            <ArrowRight size={16} />
          </a>
          <p className="mt-3 text-sm" style={{ color: 'rgba(240,244,255,0.35)' }}>No credit card required.</p>
          <div className="mt-4">
            <Link
              href="/in-action"
              className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
              style={{ color: 'rgba(0,212,255,0.70)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#00D4FF'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(0,212,255,0.70)'; }}
            >
              See AgentOS in Action →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
